import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, required } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { useModal } from 'providers/ModalProvider'
import { useAuthFlow } from 'hooks/useAuthFlow'

type ResetPasswordModalProps = {
  email?: string
  onSuccess?: () => void
  backToLogin?: JSX.Element
}

export const ResetPasswordModal = ({ backToLogin, onSuccess, email: initialEmail }: ResetPasswordModalProps) => {
  const { t } = useTranslation()
  const { close } = useModal()
  const { handleResetPassword, isLoading } = useAuthFlow()

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik({
    initialValues: {
      email: initialEmail ?? '',
    },
    validationSchema: createValidationSchema({
      email: required,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        await handleResetPassword(values, () => {
          close()
          onSuccess?.()
        })
      } catch {
        // Error already handled in hook
      }
    },
  })

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  return (
    <Modal onClose={close}>
      <ModalHeader title={t('forgottenPassword')} icon="key" />
      <ModalBody className={'tw-space-y-1'}>
        <Input
          label={t('email')}
          {...fieldProps('email')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit()
            }
          }}
        />
        <div className={'tw-h-3'} />
        <div className={'tw-text-sm'}>{t('resetPasswordHint')}</div>
        {backToLogin}
      </ModalBody>
      <ModalFooter>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={isLoading}>
          <span className={'tw-text-sm'}>{t('sendEmailResetPassword')}</span>
        </UIButton>
      </ModalFooter>
    </Modal>
  )
}
